import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearReceived, fetchReceived } from './api';
import { summarize } from './summarize';

const KIND_LABEL = {
  share: 'Share',
  note: 'Note',
  wallet: 'Wallet',
  push_sent: 'Push',
  push_error: 'Push error',
} as const;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function ReceivedList({ token }: { token: string }) {
  const client = useQueryClient();
  const key = ['spike-received', token];
  const received = useQuery({
    queryKey: key,
    queryFn: () => fetchReceived(token),
    refetchInterval: 5_000,
  });
  const clear = useMutation({
    mutationFn: () => clearReceived(token),
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });

  return (
    <section className="card" aria-labelledby="received-title" data-testid="received">
      <div className="heading-row">
        <h2 id="received-title" className="card-heading">
          Received
        </h2>
        <button
          type="button"
          disabled={clear.isPending || !received.data || received.data.items.length === 0}
          onClick={() => clear.mutate()}
        >
          Clear
        </button>
      </div>
      <p className="hint">Updates every few seconds. Newest first.</p>

      {received.isPending && <p role="status">Loading…</p>}
      {received.isError && (
        <p role="alert" className="error-text">
          Couldn't load the list.{' '}
          <button type="button" onClick={() => void received.refetch()}>
            Try again
          </button>
        </p>
      )}
      {received.data && received.data.items.length === 0 && (
        <p className="empty">
          Nothing yet. Share a link, use Siri, or make an Apple Pay purchase once the Shortcuts are
          built.
        </p>
      )}
      {received.data && received.data.items.length > 0 && (
        <ul className="received">
          {received.data.items.map((item) => (
            <li key={item.id} data-kind={item.kind}>
              <div className="received-head">
                <span className={`badge badge-${item.kind}`}>{KIND_LABEL[item.kind]}</span>
                <time dateTime={item.receivedAt}>{formatTime(item.receivedAt)}</time>
              </div>
              <p className="received-text">{summarize(item)}</p>
              <details>
                <summary>Raw data</summary>
                <pre>{JSON.stringify(item.payload, null, 2)}</pre>
              </details>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
